<template>
  <b-modal
    id="errors-modal-window"
    size="xl"
    v-model="showErrorModal"
    no-close-on-esc
    no-close-on-backdrop
    hide-footer
    hide-header
    content-class="error-modal-content"
    body-class="modal-body-particle-container d-flex flex-column justify-content-center align-items-center"
    @shown="handleModalShown"
    @hidden="handleModalHidden"
  >
    <div class="particle-viewport">
      <canvas id="particle-canvas" ref="particleCanvas"/>
      <div class="modal-content-wrapper">
        <template v-if="offline">
          <h2><font-awesome-icon icon="exclamation-triangle" class="text-danger mr-2" />Соединение потеряно</h2>
          <p>Не удалось установить соединение с сервером. Пожалуйста, проверьте ваше подключение.</p>
          <b-button size="sm" variant="secondary" @click="refreshPage">
            <font-awesome-icon icon="sync-alt" class="mr-1"/>
            Обновить страницу
          </b-button>
        </template>

        <template v-else-if="notFound">
          <h2><font-awesome-icon icon="exclamation-triangle" class="text-danger mr-2" />Страница не найдена</h2>
          <p>Ошибка 404. Такая страница не существует либо она была удалена.</p>
          <b-button size="sm" variant="secondary" @click="onBack">
            <font-awesome-icon icon="chevron-left" class="mr-1"/>
            Назад
          </b-button>
        </template>

        <template v-else-if="forbidden">
          <h2><font-awesome-icon icon="exclamation-triangle" class="text-danger mr-2" />Доступ запрещён</h2>
          <p>Ошибка 403. У вас нет прав для просмотра этой страницы, обратитесь к администратору.</p>
          <b-button size="sm" variant="secondary" @click="onBack">
            <font-awesome-icon icon="chevron-left" class="mr-1"/>
            Назад
          </b-button>
          <b-button size="sm" variant="secondary" @click="onHome">
            <font-awesome-icon icon="home" class="mr-1"/>
            Домой
          </b-button>
        </template>
      </div>
    </div>
  </b-modal>
</template>

<script>
export default {
  name: 'ErrorsModalWindow',
  props: {
    value: { type: Boolean, default: false },
    offline: { type: Boolean, required: false, default: false },
    notFound: { type: Boolean, required: false, default: false },
    forbidden: { type: Boolean, required: false, default: false },
  },
  data() {
    return {
      showErrorModal: this.value,
      particleAnimation: null,
      particleCanvas: null,
      particles: [],
      ctx: null,
      width: 0,
      height: 0
    }
  },
  mounted() {
    window.addEventListener('resize', this.handleResize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
  },
  methods: {
    refreshPage() {
      window.location.reload();
    },
    onHome() {
      this.$router.push('/home').catch(()=>{});
      this.refreshPage();
    },
    onBack() {
      if (window.history.length > 1) {
        this.$router.go(-1);
      } else {
        this.$router.push('/home').catch(()=>{});
      }
    },
    handleModalShown() {
      this.showErrorModal = true;
      this.handleResize();
    },
    handleModalHidden() {
      this.stopParticles();
      this.clearCanvas();
      this.width = 0;
      this.height = 0;
      this.showErrorModal = false;
    },
    // Canvas
    handleResize() {
      if (!this.showErrorModal) return;
      const container = this.$refs.particleCanvas ? this.$refs.particleCanvas.parentElement : null;
      if (!container) return;
      this.width = container.clientWidth;
      this.height = container.clientHeight;
    },
    startParticles() {
      // Reset
      this.stopParticles();
      this.particles = [];
      // Start
      this.particleCanvas = this.$refs.particleCanvas;
      this.particleCanvas.width = this.width;
      this.particleCanvas.height = this.height;
      this.initParticles();
    },
    initParticles() {
      this.particleCanvas = this.$refs.particleCanvas;
      this.ctx = this.particleCanvas.getContext('2d');

      if (this.particles.length === 0) {
        const particleCount = 20;
        for (let i = 0; i < particleCount; i++) {
          this.particles.push(this.createParticle());
        }
      }

      this.animateParticles();
    },
    createParticle() {
      const is404 = this.notFound;
      const isOffline = this.offline;

      const baseColor = is404
        ? `rgba(255, 165, 0, ${Math.random() * 0.6 + 0.1})`
        : `rgba(140, 0, 0, ${Math.random() * 0.6 + 0.1})`;

      return {
        x: Math.random() * this.width,
        y: Math.random() * this.height,
        size: Math.random() * 3.0 + 1.5,
        speedX: (Math.random() - 0.5) * 0.5,
        speedY: (Math.random() - 0.5) * 0.5,
        color: baseColor,
        type: is404 ? '404' : (isOffline ? 'offline' : 'forbidden'),
      };
    },
    drawParticle(p) {
      const x = p.x;
      const y = p.y;
      const radius = p.size * 2.5;

      this.ctx.strokeStyle = p.color;
      this.ctx.fillStyle = p.color;
      this.ctx.lineWidth = 1.9;

      // Circle
      this.ctx.beginPath();
      this.ctx.arc(x, y, radius, 0, Math.PI * 2);
      this.ctx.stroke();

      if (p.type === '404') {
        // Glass handle
        const handleLength = radius * 1.4;
        const handleAngle = 3 * Math.PI / 4;
        const startX = x + radius * Math.cos(handleAngle);
        const startY = y + radius * Math.sin(handleAngle);

        this.ctx.beginPath();
        this.ctx.moveTo(startX, startY);
        this.ctx.lineTo(startX + handleLength * Math.cos(handleAngle), startY + handleLength * Math.sin(handleAngle));
        this.ctx.stroke();

        // Cross
        const crossOffset = radius * 0.4;
        this.ctx.beginPath();
        this.ctx.moveTo(x - crossOffset, y - crossOffset);
        this.ctx.lineTo(x + crossOffset, y + crossOffset);
        this.ctx.moveTo(x - crossOffset, y + crossOffset);
        this.ctx.lineTo(x + crossOffset, y - crossOffset);
        this.ctx.stroke();
      } else if (p.type === 'forbidden') {
        // Diameter
        const lineOffset = radius * 0.7;
        this.ctx.beginPath();
        this.ctx.moveTo(x - lineOffset, y + lineOffset);
        this.ctx.lineTo(x + lineOffset, y - lineOffset);
        this.ctx.stroke();
      } else if (p.type === 'offline') {
        // Meridian
        this.ctx.beginPath();
        this.ctx.ellipse(x, y, radius * 0.4, radius, 0, 0, Math.PI * 2);
        this.ctx.stroke();

        // Equator
        this.ctx.beginPath();
        this.ctx.ellipse(x, y, radius, radius * 0.4, 0, 0, Math.PI * 2);
        this.ctx.stroke();

        // Slash
        const slashLength = radius * 1.0;
        this.ctx.beginPath();
        this.ctx.moveTo(x - slashLength, y + slashLength);
        this.ctx.lineTo(x + slashLength, y - slashLength);
        this.ctx.stroke();
      }
    },
    updateParticle(p) {
      p.x += p.speedX;
      p.y += p.speedY;
      // Moving beyond
      if (p.x < 0) p.x = this.width;
      if (p.x > this.width) p.x = 0;
      if (p.y < 0) p.y = this.height;
      if (p.y > this.height) p.y = 0;
    },
    animateParticles() {
      this.clearCanvas();

      this.particles.forEach(p => {
        this.updateParticle(p);
        this.drawParticle(p);
      });

      this.particleAnimation = requestAnimationFrame(this.animateParticles);
    },
    stopParticles() {
      if (this.particleAnimation) {
        cancelAnimationFrame(this.particleAnimation);
        this.particleAnimation = null;
      }
    },
    clearCanvas() {
      if (this.ctx) {
        this.ctx.clearRect(0, 0, this.width, this.height);
      }
    }
  },
  watch: {
    value(newValue) {
      this.showErrorModal = newValue;
      if (newValue) { this.handleModalShown(); }
      else { this.handleModalHidden(); }
    },
    showErrorModal(newValue) {
      this.$emit('input', newValue);
      if (!newValue) this.handleModalHidden();
    },
    width(newWidth, oldWidth) {
      if (newWidth !== oldWidth && this.showErrorModal) {
        this.startParticles();
      }
    },
    height(newHeight, oldHeight) {
      if (newHeight !== oldHeight && this.showErrorModal) {
        this.startParticles();
      }
    }
  },
}
</script>

<style>
/* MODAL */
#errors-modal-window .modal-dialog {
  max-width: 100%;
  max-height: 100%;
  width: 100%;
  height: 100%;
  margin: 0;
  padding: 0;
}

.error-modal-content {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-body-particle-container {
  padding: 0;
  height: 100%;
  width: 100%;
  position: relative;
  overflow: hidden;
}

.particle-viewport {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

#particle-canvas {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
}

.modal-content-wrapper {
  text-align: center;
  z-index: 2;
}
</style>



// WEBPACK FOOTER //
// src/components/Alerts/ErrorsModalWindow.vue